import { AvailabilitySearchTimeSlot } from "../domain_objects/AvailabilitySearchResponse";
import { TimeSlot } from "../types/TimeSlot";

export default class DateHelper {
  static getDateString(date: Date): string {
    return (
      date.getFullYear() +
      "-" +
      ("0" + (date.getMonth() + 1)).slice(-2) +
      "-" +
      ("0" + date.getDate()).slice(-2)
    );
  }

  static getShortMonthString = (m: number) => {
    return new Date(2025, m, 1).toLocaleString("default", {
      month: "short",
    });
  };

  static getTwoHourTimeSlots = (
    availableTimeSlots: TimeSlot[],
    selectedTimeSlot: TimeSlot
  ) => {
    const selectedTime = DateHelper.parseTime(
      DateHelper.sliceTimeSlot(selectedTimeSlot)
    );
    if (!selectedTime) return [];
    const twoHourTimeSlots = availableTimeSlots.filter((timeSlot) => {
      const time = DateHelper.parseTime(DateHelper.sliceTimeSlot(timeSlot));
      if (!time) return false;
      return (
        (time.getHours() >= selectedTime.getHours() - 1 &&
          time.getHours() < selectedTime.getHours() + 1) ||
        (time.getHours() === selectedTime.getHours() + 1 &&
          time.getMinutes() === 0)
      );
    });
    return twoHourTimeSlots;
  };

  static parseTime = (t: string) => {
    const d = new Date();
    const time = t.toLowerCase().match(/(\d+)(?::(\d\d))?\s*(p?)/);
    if (!time) return null;
    d.setHours(parseInt(time[1]) + (time[3] ? 12 : 0));
    d.setMinutes(parseInt(time[2]) || 0);
    return d;
  };

  static sliceTimeSlots = (timeSlots: TimeSlot[]) => {
    return timeSlots.map((t) => this.sliceTimeSlot(t));
  };

  static sliceTimeSlot = (timeSlot: TimeSlot) => {
    if (timeSlot) {
      if (!timeSlot.TimeSlot.TimeSlot.match(/T/)) {
        return timeSlot.TimeSlot.TimeSlot;
      }
      return timeSlot.TimeSlot.TimeSlot.slice(11).slice(0, -3);
    }
    return "";
  };

  static sliceAvailabilitySearchTimeSlots = (
    timeSlots: AvailabilitySearchTimeSlot[]
  ) => {
    return timeSlots.map((t) => this.sliceAvailabilitySearchTimeSlot(t));
  };

  static sliceAvailabilitySearchTimeSlot = (
    timeSlot: AvailabilitySearchTimeSlot
  ) => {
    if (!timeSlot.TimeSlot.match(/T/)) {
      return timeSlot.TimeSlot;
    }
    return timeSlot.TimeSlot.slice(11).slice(0, -3);
  };

  static findClosestTimeSlotsAfterSelectedTime(
    selectedTime: TimeSlot,
    availableTimes: TimeSlot[],
    count: number = 6 // Default to 6 closest times
  ): TimeSlot[] {
    // Convert selectedTime to Date object
    const selectedTimeAsDate = this.parseTime(
      this.sliceTimeSlot(selectedTime)
    )!;
    const selectedTimeInMinutes =
      selectedTimeAsDate.getHours() * 60 + selectedTimeAsDate.getMinutes();

    // Initialize array to track closest times after selectedTime
    const closestTimesAfter: TimeSlot[] = [];

    // Iterate through each availableTime
    availableTimes.forEach((availableTime) => {
      // Convert availableTime to Date object
      const availableTimeAsDate = this.parseTime(
        this.sliceTimeSlot(availableTime)
      )!;

      const availableTimeInMinutes =
        availableTimeAsDate.getHours() * 60 + availableTimeAsDate.getMinutes();

      // Only consider times after selectedTime
      if (availableTimeInMinutes > selectedTimeInMinutes) {
        // Calculate the difference in minutes
        const timeDiff = availableTimeInMinutes - selectedTimeInMinutes;

        // Push to closestTimesAfter if within range
        if (closestTimesAfter.length < count) {
          closestTimesAfter.push(availableTime);
        } else {
          // Replace the furthest time if closer
          const minTimeIndex = closestTimesAfter.findIndex((timeSlot) => {
            // Convert timeSlotAsDate to Date object
            const timeSlotAsDate = this.parseTime(
              this.sliceTimeSlot(timeSlot)
            )!;
            const timeSlotInMinutes =
              timeSlotAsDate.getHours() * 60 + timeSlotAsDate.getMinutes();
            return timeSlotInMinutes === availableTimeInMinutes;
          });
          if (minTimeIndex !== -1 && timeDiff < count - 1) {
            closestTimesAfter[minTimeIndex] = availableTime;
          }
        }
      }
    });

    // Return up to 'count' closest times after selectedTime
    return closestTimesAfter;
  }

  /**
   * Format the time string to be displayed in the format HH:mm using DateHelper.parseTime
   * @param availableUntil - The time string to be formatted
   * @returns The formatted time string in the format HH:mm
   */
  static formatAvailableUntilDisplayTime = (availableUntil: string) => {
    // Split the time string by colon
    const [hours, minutes] = availableUntil.split(":");
    return `${hours}:${minutes}`;
  };
}
